/** @format */

import { Component, computed, DestroyRef, inject, input, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
	Chain,
	Environment,
	MyCustomizations,
	PersonalizationState
} from '@shared/data-access/interfaces';
import {
	selectKillswitch,
	selectResolvedSkuPartNumber,
	selectSelectedPersonalizationOptions
} from '@pdp/data-access/state';
import { concatMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { AuthenticationService } from '@shared/authentication/data-access';
import { WishListService } from '@shared/favorites/data-access/services';
import { AddToCartButtonModule } from '../add-to-cart-button/add-to-cart-button.module';
import { AddToCartAlertsComponent } from '../add-to-cart-alerts/add-to-cart-alerts.component';
import { AddToListButtonComponent } from '../add-to-list-button/add-to-list-button.component';
import { selectErrorList } from '@pdp/attributes/data-access';
import { HmfAlertBannerComponent } from '@dsg-tech/homefield-angular';

@Component({
	selector: 'pdp-feature-add-to-cart-section',
	imports: [
		AddToCartButtonModule,
		AddToCartAlertsComponent,
		AddToListButtonComponent,
		HmfAlertBannerComponent
	],
	templateUrl: './add-to-cart-section.component.html',
	styleUrl: './add-to-cart-section.component.scss'
})
export class AddToCartSectionComponent implements OnInit {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly chain = inject(Chain);
	private readonly destroyRef = inject(DestroyRef);
	private readonly environment = inject(Environment);
	private readonly store = inject(Store);
	private readonly authService = inject(AuthenticationService);
	private readonly wishlistService = inject(WishListService);

	// Inputs
	readonly customizedAttributes = input<MyCustomizations>(null);
	readonly ecode = input.required<string>();
	readonly isCustomizable = input<boolean>(false);
	readonly size = input<'medium' | 'large'>('large');
	readonly isSingleSkuProduct = input<boolean>(false);

	// Emit when we want the ATC button to clear its attribute error state
	clearAttributeErrors: Subject<void> = new Subject();

	readonly favoritesEnabled = this.store.selectSignal(selectKillswitch('favoritesEnabled'));
	readonly isAttributeErrorUpdatedEnabled = this.store.selectSignal(
		selectKillswitch('isAttributeErrorUpdatedEnabled')
	);

	readonly errorListFormatter = new Intl.ListFormat('en', {
		style: 'long',
		type: 'conjunction'
	});

	readonly attributeErrors = this.store.selectSignal(selectErrorList);
	readonly attributeErrorListString = computed(() => {
		// Remove if isAttributeErrorUpdatedEnabled is ever turned off & removed
		const errorList = this.attributeErrors();

		if (!errorList) {
			return '';
		}

		// Exclude personalization errors from the error list
		const keys = Object.keys(errorList).filter((key) => key !== 'Personalization');

		return this.errorListFormatter.format(keys);
	});

	isAuthenticated: boolean;
	resolvedSkuPartNumber: string;

	listProductPersonalization: PersonalizationState | Record<string, never> = null;

	ngOnInit() {
		this.store
			.select(selectResolvedSkuPartNumber)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value: string) => {
				this.resolvedSkuPartNumber = value;
				if (value) {
					this.clearAttributeErrors.next();
				}
			});

		this.store
			.select(selectSelectedPersonalizationOptions)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				if (value) {
					this.listProductPersonalization = value;
				}
			});

		if (
			isPlatformBrowser(this.platformId) &&
			this.environment.thirdParty.auth0[this.chain.chainIdentifierAbbr].enabled
		) {
			// Check if Auth0 is enabled
			this.getIsAuthenticated();
		}
	}

	private getIsAuthenticated(): void {
		this.authService
			.getIsAuthenticated$()
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				concatMap((authResponse: boolean) => {
					this.isAuthenticated = authResponse;
					if (!this.isAuthenticated) {
						return of([]);
					}

					return this.wishlistService.getSavedItemsFromAllLists(this.ecode());
				})
			)
			.subscribe();
	}
}
